import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { InstagramGridController, InstagramGridControllerProps } from './InstagramGrid.controller';
import {
  InstagramGridPostsQuery,
  InstagramGridPostsQueryVariables,
  PostInputStatus,
  PostQueuePosition,
  PostTiming,
  SocialProfileFieldsFragment,
  useGetSocialProfilesLazyQuery,
  useInstagramGridPostsLazyQuery,
  useUpdatePostMutation,
  useUpdatePostOrderMutation
} from '../../../models/graphql';

import { AppContext } from '../../../providers/AppProvider';
import { getCache } from '../../../apollo/Cache';
import useFetchQueueWithPolling, { UseQueryPollingProps } from '../../../hooks/useFetchQueueWithPolling';
import { ApolloError, gql } from '@apollo/client';
import { showToast } from '../../../helpers/showToast';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { TopNavContext } from '../../../providers/TopNavProvider';
import { InstagramGridContext } from '../../../providers/InstagramGridProvider';
import { useRequiredQueries } from 'hooks/useRequiredQueries';
import { InstagramGridPostType } from '../../../models/post';
import { mapToInstagramGridPost } from './InstragramGridHelper';

export type InstagramGridApolloProps = {
  // ...
};

gql`
  query InstagramGridPosts($socialProfileId: ID!, $futureCount: Int) {
    instagramGridPosts(socialProfileId: $socialProfileId, futureCount: $futureCount) {
      futurePosts {
        ...PostItem
      }
      pastPosts {
        ...ThirdPartyPostFields
      }
      thirdPartyPostSearchError
    }
  }
`;

export const clearPostCache = (workspaceId: string) => {
  getCache().modify({
    id: getCache().identify({ id: workspaceId, __typename: 'Workspace' }),
    fields: {
      queue() {
        return [];
      }
    }
  });
};

export const InstagramGridApollo = (props: InstagramGridApolloProps): JSX.Element => {
  const { selectedWorkspaceId } = useContext(AppContext);
  const navContext = useContext(TopNavContext);
  const { setLoadingPosts, setLoadingPage, selectedProfileId, setSelectedProfileId, setSliderValue } =
    useContext(InstagramGridContext);

  const [instagramProfiles, setInstagramProfiles] = useState<SocialProfileFieldsFragment[]>([]);

  const { startQuery } = useFetchQueueWithPolling();

  const getSocialProfilesLazyQuery = useGetSocialProfilesLazyQuery();
  const [getSocialProfiles] = getSocialProfilesLazyQuery;

  const getInstagramGridPostsLazyQuery = useInstagramGridPostsLazyQuery({
    fetchPolicy: 'network-only'
  });

  const [getInstagramGridPosts, { data, refetch }] = getInstagramGridPostsLazyQuery;

  const [updatePost] = useUpdatePostMutation();
  const [updatePostOrder] = useUpdatePostOrderMutation();

  useRequiredQueries([getSocialProfilesLazyQuery]);

  const getInstagramSocialProfiles = useCallback(() => {
    if (!selectedWorkspaceId) {
      return;
    }

    setLoadingPosts(true);

    getSocialProfiles({ variables: { workspaceId: selectedWorkspaceId } })
      .then(results => {
        const iProfiles = results.data?.workspace.socialProfiles.filter(
          profile => profile.__typename === 'InstagramSocialProfile'
        );

        setInstagramProfiles(() => iProfiles || []);

        // When no profiles, remove loading since processing will stop
        if (!iProfiles?.length) {
          setLoadingPage(false);
        }
      })
      .finally(() => setLoadingPosts(false));
  }, [getSocialProfiles, selectedWorkspaceId, setLoadingPage, setLoadingPosts]);

  const getInstagramPosts = useCallback(async () => {
    if (!selectedWorkspaceId) {
      return;
    }

    setLoadingPosts(true);

    const options = { socialProfileId: selectedProfileId, futureCount: 50 };
    const UseQueryPollingProps: UseQueryPollingProps<InstagramGridPostsQuery, InstagramGridPostsQueryVariables> = {
      queryFn: getInstagramGridPosts,
      options
    };

    await startQuery(UseQueryPollingProps)
      .catch((error: ApolloError) => {
        // TODO: Verify this message and/or message handling
        showToast({
          severity: 'error',
          summary: 'Could not get posts',
          detail: remapApolloErrors(error)
        });
      })
      .finally(() => {
        setLoadingPage(false);
        setLoadingPosts(false);
      });
  }, [getInstagramGridPosts, selectedProfileId, selectedWorkspaceId, setLoadingPage, setLoadingPosts, startQuery]);

  const getPostScheduleListItems = useCallback(async () => {
    // catches rate timing edge case where selectedProfileId is not yet set
    if (!selectedProfileId) {
      return;
    }

    setLoadingPosts(true);
    clearPostCache(selectedWorkspaceId);

    getInstagramPosts().finally(() => setLoadingPosts(false));
  }, [getInstagramPosts, selectedProfileId, selectedWorkspaceId, setLoadingPosts]);

  const movePostPosition = useCallback(
    (postId: string, position: PostQueuePosition) => {
      updatePost({
        variables: {
          postId: postId,
          input: { timing: { type: PostTiming.QUEUE, queue: { queuePosition: position } } }
        },
        update: () => {
          getPostScheduleListItems().finally();
        }
      }).catch((error: ApolloError) => {
        showToast({
          severity: 'error',
          summary: 'Could not move the post',
          detail: remapApolloErrors(error)
        });
      });
    },
    [getPostScheduleListItems, updatePost]
  );

  const trashSinglePost = useCallback(
    (postId: string) => {
      updatePost({
        variables: {
          postId: postId,
          input: {
            status: PostInputStatus.TRASH
          }
        },
        update: () => {
          setSliderValue(oldValue => (oldValue ? oldValue - 1 : oldValue));
          getPostScheduleListItems().finally();
        }
      })
        .then(() => {
          showToast({
            severity: 'success',
            summary: '',
            detail: 'Post moved to Trash'
          });
        })
        .catch((error: ApolloError) => {
          showToast({
            severity: 'error',
            summary: 'Could not delete the post',
            detail: remapApolloErrors(error)
          });
        });
    },
    [getPostScheduleListItems, setSliderValue, updatePost]
  );

  const refetchPosts = () => {
    setLoadingPosts(true);

    refetch().finally(() => setLoadingPosts(false));
  };

  useEffect(() => {
    if (!selectedProfileId && instagramProfiles.length > 0) {
      setSelectedProfileId(() => instagramProfiles[0].id);
    }
  }, [instagramProfiles, selectedProfileId, setSelectedProfileId]);

  useEffect(() => {
    getInstagramSocialProfiles();
  }, [getInstagramSocialProfiles, selectedWorkspaceId]);

  useEffect(() => {
    if (selectedProfileId) {
      getPostScheduleListItems().finally();
    }
  }, [getPostScheduleListItems, selectedProfileId, setLoadingPosts]);

  const handleNewPostClose = useCallback(
    (status: boolean) => {
      if (status) {
        getPostScheduleListItems().finally();
      }
    },
    [getPostScheduleListItems]
  );

  // We add conditional checking of the callback to prevent `Maximum update depth` issues
  useEffect(() => {
    if (navContext.state.newPostCloseCallback !== handleNewPostClose) {
      navContext.dispatch({
        type: 'setNewPostCloseCallback',
        payload: handleNewPostClose
      });
    }

    return () => {
      if (navContext.state.newPostCloseCallback === handleNewPostClose) {
        navContext.dispatch({
          type: 'setNewPostCloseCallback',
          payload: undefined
        });
      }
    };
  }, [handleNewPostClose, navContext]);

  const igGridPosts: InstagramGridPostType[] = useMemo(() => {
    const pastPosts = data?.instagramGridPosts.pastPosts?.map(mapToInstagramGridPost) ?? [];
    const futurePosts = data?.instagramGridPosts.futurePosts?.map(mapToInstagramGridPost) ?? [];

    return [...futurePosts, ...pastPosts];
  }, [data]);

  const controllerProps: InstagramGridControllerProps = {
    ...props,
    instagramProfiles,
    getPostScheduleListItems,
    getInstagramSocialProfiles,
    refetchPosts,
    updatePostOrder,
    movePostPosition,
    trashSinglePost,
    igGridPosts,
    igApiError: data?.instagramGridPosts.thirdPartyPostSearchError ?? undefined
  };

  return <InstagramGridController {...controllerProps} />;
};
